import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'

import { direccionDeIngreso } from '@/lib/auth/usuario'
import { detenerRastreoAndroid } from '@/lib/nativo/rastreoAndroid'
import { detenerPushNativo } from '@/lib/nativo/pushNativo'
import { supabase } from '@/lib/supabase/cliente'
import type { Perfil, PermisoModulo } from '@/lib/supabase/tipos-bd'

import { ContextoSesion, type AccionPermiso, type EstadoSesion } from './contexto'

/**
 * Mantiene viva la sesión del usuario y sus permisos.
 *
 * Envuelve toda la aplicación. Es el único sitio que habla con Supabase Auth:
 * ningún módulo del negocio debería llamar a supabase.auth directamente.
 */
/*
 * LA ÚLTIMA SESIÓN CONOCIDA, PARA PODER ABRIR SIN SEÑAL.
 *
 * Se guarda por usuario: si en el mismo teléfono entra otra persona, no
 * hereda el perfil ni los permisos del anterior.
 */
const LLAVE_SESION = 'mega.sesion'

type SesionGuardada = { usuarioId: string; perfil: Perfil; permisos: PermisoModulo[] }

function guardarSesion(usuarioId: string, perfil: Perfil, permisos: PermisoModulo[]) {
  try {
    localStorage.setItem(LLAVE_SESION, JSON.stringify({ usuarioId, perfil, permisos }))
  } catch {
    /* Sin espacio o en modo privado: se sigue, solo se pierde el modo sin señal. */
  }
}

function leerSesionGuardada(usuarioId: string): SesionGuardada | null {
  try {
    const crudo = localStorage.getItem(LLAVE_SESION)
    if (!crudo) return null
    const guardado = JSON.parse(crudo) as SesionGuardada
    return guardado.usuarioId === usuarioId ? guardado : null
  } catch {
    return null
  }
}

/**
 * El usuario de la sesión que sigue guardada en el teléfono, aunque su token
 * haya vencido.
 *
 * Sin señal y con el token de más de una hora, `getSession()` intenta
 * renovarlo, no puede, y contesta «no hay sesión» —pero NO la borra: la
 * guarda para renovarla cuando vuelva la red—. La app tomaba ese «no hay» al
 * pie de la letra y mandaba al técnico a la pantalla de ingreso, donde sin
 * señal no se puede entrar. Es decir: abrir la app en la obra a media mañana
 * no servía (auditoría sin conexión, oct 2026).
 *
 * Se lee directo del almacenamiento solo para saber QUIÉN es y abrir con su
 * perfil guardado. Nada se le pide al servidor con esto: en cuanto haya red,
 * la librería renueva el token sola y las colas salen con el token bueno.
 */
function usuarioDeLaSesionGuardada(): string | null {
  try {
    const crudo = localStorage.getItem('mega-ordenes-sesion')
    if (!crudo) return null
    const sesion = JSON.parse(crudo) as { user?: { id?: string }; refresh_token?: string }
    return sesion.refresh_token && sesion.user?.id ? sesion.user.id : null
  } catch {
    return null
  }
}

/** ¿El error es de no poder hablar con el servidor, no una respuesta suya? */
function esDeRed(error: { name?: string; message?: string; status?: number } | null): boolean {
  if (!error) return false
  if (typeof navigator !== 'undefined' && !navigator.onLine) return true
  return (
    error.name === 'AuthRetryableFetchError' ||
    (!error.status && /fetch|network|timeout|load failed/i.test(error.message ?? '')) ||
    (error.status ?? 0) >= 500
  )
}

function olvidarSesionGuardada() {
  try {
    localStorage.removeItem(LLAVE_SESION)
  } catch {
    /* Da igual: lo que manda es la sesión real. */
  }
}

export function ProveedorSesion({ children }: { children: ReactNode }) {
  const [cargando, setCargando] = useState(true)
  const [perfil, setPerfil] = useState<Perfil | null>(null)
  const [permisos, setPermisos] = useState<PermisoModulo[]>([])

  /**
   * Trae el perfil y los permisos del usuario autenticado.
   *
   * Un usuario puede existir en Supabase Auth y estar desactivado en la
   * empresa. En ese caso se cierra la sesión de inmediato: entrar y ver una
   * pantalla vacía es peor que no entrar.
   */
  const cargarPerfil = useCallback(async (usuarioId: string) => {
    /*
     * SIN SEÑAL SE ENTRA CON LO ÚLTIMO QUE SE SUPO.
     *
     * Esta función consulta el servidor, y sin red no devuelve nada: la
     * aplicación se quedaba en «Verificando la sesión…» para siempre. Se
     * comprobó cortando la red y recargando: la app abría —el service worker
     * la sirve— pero no pasaba de esa pantalla, así que en la obra no servía
     * de nada.
     *
     * Ahora el perfil y los permisos se guardan al entrar con señal y se usan
     * cuando no la hay. No es un atajo de seguridad: con o sin red, lo que
     * de verdad protege los datos son las políticas de la base, que se
     * aplican en el servidor con el token de quien pide. Esto solo decide
     * qué botones ve el técnico mientras está incomunicado.
     */
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      const guardado = leerSesionGuardada(usuarioId)
      if (guardado) {
        setPerfil(guardado.perfil)
        setPermisos(guardado.permisos)
      }
      return
    }

    const { data: perfilCargado, error } = await supabase
      .from('perfiles')
      .select('*')
      .eq('id', usuarioId)
      .maybeSingle()

    /*
     * SOLO SE ECHA A ALGUIEN CUANDO EL SERVIDOR LO DICE.
     *
     * Antes cualquier error cerraba la sesión, y solo se perdonaba si el
     * teléfono decía estar desconectado. Con la raya que no transmite —tan
     * común en obra— el teléfono dice que sí hay red, la consulta falla, y se
     * llamaba a signOut(): el técnico quedaba fuera, y como esto corre cada
     * vez que vuelve a la app, bastaba sacar el teléfono del bolsillo en un
     * sótano. Ahora un ERROR nunca cierra la sesión: se sigue con lo guardado
     * y se reintenta la próxima vez. Solo se cierra si el servidor contesta
     * que el perfil no existe o está desactivado.
     */
    if (error) {
      const guardado = leerSesionGuardada(usuarioId)
      if (guardado) {
        setPerfil(guardado.perfil)
        setPermisos(guardado.permisos)
      }
      return
    }

    if (!perfilCargado || !perfilCargado.activo) {
      await supabase.auth.signOut()
      setPerfil(null)
      setPermisos([])
      olvidarSesionGuardada()
      return
    }

    const { data: permisosCargados } = await supabase
      .from('permisos_modulo')
      .select('*')
      .eq('rol', perfilCargado.rol)

    setPerfil(perfilCargado)
    setPermisos(permisosCargados ?? [])
    guardarSesion(usuarioId, perfilCargado, permisosCargados ?? [])
  }, [])

  useEffect(() => {
    let vigente = true

    // Abrir con el perfil guardado de quien tiene la sesión en el teléfono.
    const abrirConLoGuardado = () => {
      const usuarioId = usuarioDeLaSesionGuardada()
      const guardado = usuarioId ? leerSesionGuardada(usuarioId) : null
      if (guardado) {
        setPerfil((actual) => actual ?? guardado.perfil)
        setPermisos((actuales) => (actuales.length ? actuales : guardado.permisos))
      }
    }

    /*
     * CON PLAZO. Sin red y con el token vencido, getSession() no contesta
     * enseguida: la librería reintenta renovar el token con esperas cada vez
     * más largas, hasta medio minuto. La prueba sin señal lo midió: la app se
     * quedaba en «Verificando la sesión…». Si no contesta a tiempo, se abre
     * con lo guardado y la respuesta, cuando llegue, actualiza lo que haga
     * falta.
     */
    const plazo = setTimeout(
      () => {
        if (!vigente) return
        abrirConLoGuardado()
        setCargando(false)
      },
      typeof navigator !== 'undefined' && !navigator.onLine ? 800 : 6000,
    )

    // Sesión guardada de una visita anterior.
    void supabase.auth
      .getSession()
      .then(async ({ data, error }) => {
        if (!vigente) return
        if (data.session?.user) {
          await cargarPerfil(data.session.user.id)
          return
        }
        // Token vencido y sin red: la sesión sigue en el teléfono. Se abre
        // con el perfil guardado (ver usuarioDeLaSesionGuardada).
        if (esDeRed(error)) abrirConLoGuardado()
      })
      // PASE LO QUE PASE SE DEJA DE ESPERAR. Si esto falla —sin red, el
      // servidor caído—, quedarse en «Verificando la sesión…» es una pantalla
      // muerta de la que no se sale ni recargando.
      .finally(() => {
        clearTimeout(plazo)
        if (vigente) setCargando(false)
      })

    /*
     * LOS PERMISOS SE RELEEN AL VOLVER A LA PESTAÑA.
     *
     * Se cargaban una sola vez al entrar, así que un permiso concedido después
     * no existía para quien ya estaba dentro: la pantalla le seguía escondiendo
     * los botones y no había forma de adivinar por qué. Le pasó a Jerson —se le
     * dieron los permisos de gerencia y siguió sin ver «Cotizar de nuevo» ni
     * «Anular» hasta cerrar sesión—, y con un cambio de rol pasa igual.
     *
     * Al volver a la pestaña se vuelven a pedir: son dos consultas pequeñas y
     * solo cuando alguien regresa a la ventana. Nadie tiene que saber que hay
     * que volver a entrar.
     */
    const alVolver = () => {
      if (document.visibilityState !== 'visible') return
      void supabase.auth.getSession().then(({ data }) => {
        if (vigente && data.session?.user) void cargarPerfil(data.session.user.id)
      })
    }
    window.addEventListener('focus', alVolver)
    document.addEventListener('visibilitychange', alVolver)

    // Cambios posteriores: ingreso, salida, token renovado en otra pestaña.
    const { data: suscripcion } = supabase.auth.onAuthStateChange((evento, sesion) => {
      if (!vigente) return

      // Solo una salida de verdad vacía el perfil. «INITIAL_SESSION» sin
      // sesión llega también cuando el token venció sin red, y vaciarlo ahí
      // deshacía la apertura con lo guardado.
      if (evento === 'SIGNED_OUT') {
        setPerfil(null)
        setPermisos([])
        return
      }
      if (!sesion?.user) return

      // TOKEN_REFRESHED llega cada hora: recargar el perfil ahí sería una
      // consulta inútil y un parpadeo en la interfaz.
      if (evento === 'SIGNED_IN' || evento === 'USER_UPDATED') {
        void cargarPerfil(sesion.user.id)
      }
    })

    return () => {
      vigente = false
      clearTimeout(plazo)
      window.removeEventListener('focus', alVolver)
      document.removeEventListener('visibilitychange', alVolver)
      suscripcion.subscription.unsubscribe()
    }
  }, [cargarPerfil])

  const puede = useCallback(
    (modulo: string, accion: AccionPermiso): boolean => {
      if (!perfil) return false

      // Mismo criterio que fn_puede() en la base: el administrador de ARKANO
      // no se bloquea a sí mismo durante la puesta en marcha.
      if (perfil.rol === 'administrador') return true

      return permisos.find((p) => p.modulo === modulo)?.[accion] ?? false
    },
    [perfil, permisos],
  )

  const ingresar = useCallback(async (usuario: string, contrasena: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      // Se entra por usuario. La dirección de correo con la que viaja es un
      // apaño interno que arma direccionDeIngreso y que no se le muestra a
      // nadie; ver src/lib/auth/usuario.ts.
      email: direccionDeIngreso(usuario),
      password: contrasena,
    })

    if (error) {
      if (error.message !== 'Invalid login credentials') throw new Error(error.message)

      // EL MENSAJE NO PUEDE ACUSAR AL CAMPO QUE ESTABA BIEN.
      //
      // Aquí se le decía a quien escribiera un correo que ese era el error
      // —«aquí va tu usuario, no tu correo»— y eso es FALSO: direccionDeIngreso
      // respeta el correo tal cual, y hay cuentas que entran así a propósito
      // (la de ARKANO, la de gerencia). A quien se equivocaba de contraseña se
      // le mandaba a corregir lo único que tenía bien, y no volvía a mirar la
      // contraseña. Justo el desvío que el mensaje decía querer evitar.
      //
      // Tampoco se dice cuál de los dos campos falló: confirmar que un usuario
      // existe le sirve a cualquiera que esté probando de a uno.
      throw new Error('Usuario o contraseña incorrectos.')
    }
  }, [])

  const salir = useCallback(async () => {
    // Si es la app nativa, apaga el servicio de ubicación antes de cerrar sesión:
    // sin sesión no puede reportar, y no tiene por qué seguir encendido.
    void detenerRastreoAndroid()
    // Y da de baja el token de push de este aparato: sin sesión no debe seguir
    // recibiendo los avisos de la empresa en este teléfono.
    void detenerPushNativo()
    await supabase.auth.signOut()
    setPerfil(null)
    setPermisos([])
    // Y se borra la copia local: salir tiene que dejar el teléfono limpio,
    // también para el modo sin señal.
    olvidarSesionGuardada()
  }, [])

  const valor = useMemo<EstadoSesion>(
    () => ({ cargando, perfil, permisos, puede, ingresar, salir }),
    [cargando, perfil, permisos, puede, ingresar, salir],
  )

  return <ContextoSesion.Provider value={valor}>{children}</ContextoSesion.Provider>
}
